import { inject, Injectable } from '@angular/core';
import { ToastService } from '@hunting-nature/Secacam/shared/services/toast.service';
import type { AlbumData, UserCameras } from '@hunting-nature/shared-core/interfaces/album.interface';
import { createLogger } from '@hunting-nature/shared-core/logging';
import { ModalController, NavController } from '@ionic/angular/standalone';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, forkJoin, from, map, of, switchMap, take, tap } from 'rxjs';
import { GalleryActions } from '../../image-gallery/redux/gallery.actions';
import { GalleryService } from '../../image-gallery/services/gallery.service';
import { GalleryPageService } from '../../services/gallery.page.service';
import { AlbumActions } from './album.actions';

@Injectable()
export class AlbumEffects {
  // services
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly galleryService = inject(GalleryService);
  private readonly galleryPageService = inject(GalleryPageService);
  private readonly modalController = inject(ModalController);
  private readonly navController = inject(NavController);
  private readonly log = createLogger('AlbumEffects');
  private readonly toastService = inject(ToastService);

  albumsPageEntered$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlbumActions.albumsPageEntered),
      exhaustMap(() => {
        // Step 1: start each load routine independently so every request can dispatch its own load and success/failure actions as it settles.
        const preDefinedAlbumsLoaded$ = of(null).pipe(
          // Emit the "loading started" action, then call the service.
          tap(() => this.store.dispatch(AlbumActions.albumsPageEnteredPreDefinedAlbumsLoaded())),
          switchMap(() => this.galleryService.getPreDefinedAlbums$()),
          // Emit the success action as soon as this request returns.
          tap(albums => this.store.dispatch(AlbumActions.albumsPageEnteredPreDefinedAlbumsLoadedSucceeded({ albums }))),
          map(() => true),
          catchError((error: unknown) => {
            // Convert errors into a failed action and a boolean result so the page-level forkJoin can still wait for all routines to settle.
            this.store.dispatch(AlbumActions.albumsPageEnteredPreDefinedAlbumsLoadedFailed({ error: error as Error }));
            return of(false);
          })
        );

        const userDefinedAlbumsLoaded$ = of(null).pipe(
          // Emit the "loading started" action, then call the service.
          tap(() => this.store.dispatch(AlbumActions.albumsPageEnteredUserDefinedAlbumsLoaded())),
          switchMap(() => this.galleryService.getUserDefinedAlbums$()),
          // Emit the success action as soon as this request returns.
          tap(album => this.store.dispatch(AlbumActions.albumsPageEnteredUserDefinedAlbumsLoadedSucceeded({ album }))),
          map(() => true),
          catchError((error: unknown) => {
            // Convert errors into a failed action and a boolean result so the page-level forkJoin can still wait for all routines to settle.
            this.store.dispatch(AlbumActions.albumsPageEnteredUserDefinedAlbumsLoadedFailed({ error: error as Error }));
            return of(false);
          })
        );

        const ownAndSharedCamerasLoaded$ = of(null).pipe(
          // Emit the "loading started" action, then call the service.
          tap(() => this.store.dispatch(AlbumActions.albumsPageEnteredOwnAndSharedCamerasLoaded())),
          switchMap(() => this.galleryPageService.getOwnAndSharedCameraAlbums$().pipe(take(1))),
          // Emit the success action as soon as this request returns.
          tap(cameras => this.store.dispatch(AlbumActions.albumsPageEnteredOwnAndSharedCamerasLoadedSucceded({ cameras }))),
          map(() => true),
          catchError((error: unknown) => {
            // Convert errors into a failed action and a boolean result so the page-level forkJoin can still wait for all routines to settle.
            this.store.dispatch(AlbumActions.albumsPageEnteredOwnAndSharedCamerasLoadedFailed({ error: error as Error }));
            return of(false);
          })
        );

        // Step 2: wait until all three routines have settled, then emit the page-level terminal action based on their combined outcome.
        return forkJoin([preDefinedAlbumsLoaded$, userDefinedAlbumsLoaded$, ownAndSharedCamerasLoaded$]).pipe(
          map(results => (results.every(Boolean) ? AlbumActions.albumsPageEnteredSucceeded() : AlbumActions.albumsPageEnteredFailed())),
          catchError(() => of(AlbumActions.albumsPageEnteredFailed()))
        );
      })
    )
  );

  refreshAlbumsPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlbumActions.refreshAlbumsPage),
      exhaustMap(() => {
        // Step 1: start each refresh routine independently so every request can dispatch its own load and success/failure actions as it settles.
        const preDefinedAlbumsLoaded$ = of(null).pipe(
          // Emit the "loading started" action, then call the service.
          tap(() => this.store.dispatch(AlbumActions.refreshAlbumsPagePreDefinedAlbumsLoaded())),
          switchMap(() => this.galleryService.getPreDefinedAlbums$()),
          // Emit the success action as soon as this request returns.
          tap(albums => this.store.dispatch(AlbumActions.refreshAlbumsPagePreDefinedAlbumsLoadedSucceeded({ albums }))),
          map(() => true),
          catchError((error: unknown) => {
            // Convert errors into a failed action and a boolean result so the page-level forkJoin can still wait for all routines to settle.
            this.store.dispatch(AlbumActions.refreshAlbumsPagePreDefinedAlbumsLoadedFailed({ error: error as Error }));
            return of(false);
          })
        );

        const userDefinedAlbumsLoaded$ = of(null).pipe(
          // Emit the "loading started" action, then call the service.
          tap(() => this.store.dispatch(AlbumActions.refreshAlbumsPageUserDefinedAlbumsLoaded())),
          switchMap(() => this.galleryService.getUserDefinedAlbums$()),
          // Emit the success action as soon as this request returns.
          tap(album => this.store.dispatch(AlbumActions.refreshAlbumsPageUserDefinedAlbumsLoadedSucceeded({ album }))),
          map(() => true),
          catchError((error: unknown) => {
            // Convert errors into a failed action and a boolean result so the page-level forkJoin can still wait for all routines to settle.
            this.store.dispatch(AlbumActions.refreshAlbumsPageUserDefinedAlbumsLoadedFailed({ error: error as Error }));
            return of(false);
          })
        );

        const ownAndSharedCamerasLoaded$ = of(null).pipe(
          // Emit the "loading started" action, then call the service.
          tap(() => this.store.dispatch(AlbumActions.refreshAlbumsPageOwnAndSharedCamerasLoaded())),
          switchMap(() => this.galleryPageService.getOwnAndSharedCameraAlbums$().pipe(take(1))),
          // Emit the success action as soon as this request returns.
          tap(cameras => this.store.dispatch(AlbumActions.refreshAlbumsPageOwnAndSharedCamerasLoadedSucceded({ cameras }))),
          map(() => true),
          catchError((error: unknown) => {
            // Convert errors into a failed action and a boolean result so the page-level forkJoin can still wait for all routines to settle.
            this.store.dispatch(AlbumActions.refreshAlbumsPageOwnAndSharedCamerasLoadedFailed({ error: error as Error }));
            return of(false);
          })
        );

        // Step 2: wait until all three routines have settled, then emit the refresh-level terminal action based on their combined outcome.
        return forkJoin([preDefinedAlbumsLoaded$, userDefinedAlbumsLoaded$, ownAndSharedCamerasLoaded$]).pipe(
          map(results => (results.every(Boolean) ? AlbumActions.refreshAlbumsPageSucceeded() : AlbumActions.refreshAlbumsPageFailed())),
          catchError(() => of(AlbumActions.refreshAlbumsPageFailed()))
        );
      })
    )
  );

  loadUserDefinedAlbumsToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AlbumActions.albumsPageEnteredUserDefinedAlbumsLoadedFailed),
        tap(() => this.toastService.showTranslatedError('errors.gallery.fetchAlbums'))
      ),
    { dispatch: false }
  );

  refreshUserDefinedAlbumsToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AlbumActions.refreshAlbumsPageUserDefinedAlbumsLoadedFailed),
        tap(() => this.toastService.showTranslatedError('errors.gallery.refreshAlbums'))
      ),
    { dispatch: false }
  );

  loadMoreUserDefinedAlbums$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlbumActions.loadMoreUserDefinedAlbums),
      switchMap(action =>
        this.galleryService.getUserDefinedAlbums$(action.startIndex).pipe(
          map((albumData: AlbumData) =>
            AlbumActions.loadMoreUserDefinedAlbumsSucceeded({
              album: albumData,
            })
          ),
          catchError((error: unknown) => {
            this.log.error('Loading more user defined albums failed', { error });
            return of(AlbumActions.loadMoreUserDefinedAlbumsFailed({ error: error as Error }));
          })
        )
      )
    )
  );

  loadOwnAndSharedCamerasToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AlbumActions.albumsPageEnteredOwnAndSharedCamerasLoadedFailed),
        tap(() => this.toastService.showTranslatedError('errors.gallery.fetchCameras'))
      ),
    { dispatch: false }
  );

  refreshOwnAndSharedCamerasToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AlbumActions.refreshAlbumsPageOwnAndSharedCamerasLoadedFailed),
        tap(() => this.toastService.showTranslatedError('errors.gallery.refreshCameras'))
      ),
    { dispatch: false }
  );

  loadMoreOwnedAndSharedCameras$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlbumActions.loadMoreOwnAndSharedCameras),
      switchMap(action =>
        this.galleryPageService.getOwnAndSharedCameraAlbums$(action.startIndex).pipe(
          map((data: UserCameras) =>
            AlbumActions.loadMoreOwnAndSharedCamerasSucceeded({
              cameras: data,
            })
          ),
          catchError((error: unknown) => {
            this.log.error('Loading more owned and shared cameras failed', { error });
            return of(AlbumActions.loadMoreOwnAndSharedCamerasFailed({ error: error as Error }));
          })
        )
      )
    )
  );

  closeImageViewerAndOpenCameraDetails$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AlbumActions.closeImageViewerAndOpenCameraDetails),
        switchMap(action =>
          from(this.modalController.getTop()).pipe(
            switchMap(modal => {
              return modal ? from(modal.dismiss()).pipe(map(() => action)) : of(action);
            })
          )
        ),
        switchMap(action => this.navController.navigateForward(['/home/cameras', action.cameraId, action.cameraName]))
        /*
        tap(
          (action: any) => {
            of(null)
              .pipe(
                delay(10),
                tap(() => {
                  this.router.navigate(['/home/cameras', action.cameraId]);
                })
              )
              .subscribe();
          }

          // of(null)

        )
          */
      ),
    { dispatch: false }
  );

  openAlbumAction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlbumActions.openAlbum),
      tap(action => void this.navController.navigateForward([`/home/gallery/${action.albumId}`])),
      // TODO: refactor redux
      switchMap(() => of(GalleryActions.clearFilteredImages()))
    )
  );

  showAllImages$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AlbumActions.showAllImages),
        switchMap(_action => this.navController.navigateForward([`/home/gallery/all`]))
      ),
    { dispatch: false }
  );

  showFavoriteImages$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AlbumActions.showFavoriteImages),
        switchMap(_action => this.navController.navigateForward([`/home/gallery/favorites`]))
      ),
    { dispatch: false }
  );
}
